/*
	Controller class for input duo "price"-"currency"
 */

import {Money} from "./Money.js";

/**
 * Represents form-global state of calculation
 */
export class CalcState {
	constructor(vatBase) {
		if (typeof vatBase === 'undefined') this.vatBase = Server.vatBase
		else this.vatBase = vatBase;
	}

	setVatBase(value) {
		this.vatBase = value;
		const arrows = $('.tax-dir');
		if (this.vatBase) arrows.addClass("reverse");
		else arrows.removeClass("reverse");
	}
}


export class ObservableField {
	constructor() {
		this.handlers = [];
	}

	/**
	 * Register a callback triggered if one of the two underlying fields is edited
	 * @param {function} callback
	 */
	onChange(callback) {
		this.handlers.push(callback);
	}

	/**
	 * Invoke all listeners waiting for the change
	 */
	trigger() {
		const name = this.name;
		for (let handler of this.handlers) {
			handler(name, this);
		}
	}

	get name() {
		return "";
	}
}


/**
 * Pair of amount/currency input fields
 */
export class MoneyField extends ObservableField {
	/**
	 * @param {CalcState} state - global calculation state
	 * @param {string|jQuery} amount	- DOM ID of input containing amount or jQuery representing that input
	 * @param {string|jQuery} [currency] - DOM ID of input containing currency or jQuery representing that input
	 */
	constructor(state, amount, currency) {
		super();
		this.state = state;
		this.amountInput = typeof amount === 'string' ? $('#' + amount) : amount;
		if (typeof currency !== 'undefined') this.currencyInput = typeof currency === 'string' ? $('#' + currency) : currency;

		this.amountInput.val(this.toMoney().format());	// correction (can be removed after server refactoring from double->BigDecimal)

		const target = this.trigger.bind(this);
		this.amountInput.on('input', target).on('focus', ()=>{ this.state.setVatBase(this.amountInput.hasClass("price-tax")); });
		if (this.currencyInput) this.currencyInput.on('change', target);
	}

	get currency() {
		if (this.currencyInput) return this.currencyInput.val();
		return Server.defaultCurrency;
	}

	/**
	 * @return {Money}
	 */
	toMoney() {
		return Money.of(Money.parse(this.amountInput.val()), this.currency);
	}

	get name() {
		return this.amountInput.attr("id");
	}

	set amount(value) {
		this.amountInput.val(value);
	}
}


export class MoneyTaxField extends ObservableField {

	/**
	 * @param {MoneyField} price	- field with price excl. VAT
	 * @param {MoneyField} priceTax	- field with price incl. VAT
	 * @param {MoneyField} [tax]	- field with just VAT amount
	 * @param {string|jQuery} vat	- vat select ID, or jQuery object with the select directly
	 */
	constructor(price, priceTax, tax = null, vat) {
		super();
		this.price = price;
		this.priceTax = priceTax;
		this.tax = tax;
		this.vatInput = typeof vat === 'string' ? $('#' + vat) : vat;

		const target = this.trigger.bind(this);
		this.price.onChange(target);
		this.priceTax.onChange(target);
		if (this.tax !== null) {
			this.tax.onChange(target);
			const vat = this.priceTax.toMoney().subtract(this.price.toMoney());	// calc VAT
			this.tax.amount = vat.format();
		}
		this.vatInput.on('change', target);
		this.validate();
	}

	get name() {
		return "MoneyTaxField." + this.price.name;
	}

	/**
	 * @returns {CalcState}
	 */
	get state() {
		return this.price.state;
	}

	get taxRate() {
		return Server.VAT[parseInt(this.vatInput.val())];
	}

	/**
	 * Check if the two/three managed input fields contains correct values and add .invalid class if not
	 */
	validate() {
		const rate = this.taxRate / 100.0;
		const basePrice = this.price.toMoney();
		const expectedTaxPrice = basePrice.multiply(1.0+rate);
		let invalid = Math.abs(this.priceTax.toMoney().amount - expectedTaxPrice.amount) > 2;
		if (this.tax !== null) {
			const expectedTax = expectedTaxPrice.subtract(basePrice);
			invalid = invalid || Math.abs(this.tax.toMoney().amount - expectedTax.amount) > 2;
		}
		if (invalid) {
			this.price.amountInput.addClass("invalid");
			this.priceTax.amountInput.addClass("invalid");
			if (this.tax !== null) this.tax.amountInput.addClass("invalid");
		}
	}

	clearInvalid() {
		this.price.amountInput.removeClass("invalid");
		this.priceTax.amountInput.removeClass("invalid");
		if (this.tax !== null) this.tax.amountInput.removeClass("invalid");
	}

	checkNegative() {
		if (this.price.toMoney().amount < 0) this.price.amountInput.addClass("neg");
		else this.price.amountInput.removeClass("neg");

		if (this.priceTax.toMoney().amount < 0) this.priceTax.amountInput.addClass("neg");
		else this.priceTax.amountInput.removeClass("neg");

		if (this.tax !== null) {
			if (this.tax.toMoney().amount < 0) this.tax.amountInput.addClass("neg");
			else this.tax.amountInput.removeClass("neg");
		}
	}

	trigger() {
		this.clearInvalid();
		const rate = this.taxRate / 100.0;
		let targetMoney, money;
		if (this.state.vatBase) {
			money = this.priceTax.toMoney().multiply(1.0 / (1.0 + rate));	// base money from tax money
			targetMoney = this.price;
		} else {
			money = this.price.toMoney().multiply(1.0+rate);	// tax money from base money
			targetMoney = this.priceTax;
		}
		targetMoney.amount = money.format();
		if (this.tax !== null) {
			const vat = this.priceTax.toMoney().subtract(this.price.toMoney());
			this.tax.amount = vat.format();
		}
		this.checkNegative();
		super.trigger();
	}

	updateTaxFromBase() {
		this.clearInvalid();
		const rate = this.taxRate / 100.0;
		const money = this.price.toMoney().multiply(1.0+rate);
		this.priceTax.amount = money.format();
		this.checkNegative();
	}

	updateBaseFromTax() {
		this.clearInvalid();
		const rate = this.taxRate / 100.0;
		const money = this.priceTax.toMoney().multiply(1.0 / (1.0 + rate));
		this.price.amount = money.format();
		this.checkNegative();
	}
}
